import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import Badge from "ui/Badge";
import Grid from "ui/Grid";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PageContent from "ui/PageContent";
import SettingsNav from "ui/SettingsNav";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";
import { makeFilter } from "utils/filter";
import { useRoutes } from "utils/routes";

type RowDataType = {
  id: string;
  name: string;
  type: "Cluster" | "Server Group";
  clusterId: string | null;
};

const ServerGroupSettingsList = () => {
  const currentOrganization = useCurrentOrganization();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const { organization } = useRoutes();
  const { serverId } = useParams();

  if (currentOrganization == null) {
    return <Loading error />;
  }

  if (!currentOrganization.enabledFeatures.serverGroupCrud) {
    return <Navigate to={organization(currentOrganization.slug)} replace />;
  }

  const sourceGroups = serverId
    ? currentOrganization.serverGroups.filter((sg) =>
        sg.servers.some((s) => s.humanId === serverId),
      )
    : currentOrganization.serverGroups;

  const gridData: RowDataType[] = sourceGroups
    .map(
      (sg): RowDataType => ({
        id: sg.id,
        name: sg.name,
        type: sg.clusterId != null ? "Cluster" : "Server Group",
        clusterId: sg.clusterId,
      }),
    )
    .filter(makeFilter(searchTerm, "name"));

  return (
    <PageContent
      title="Settings"
      pageCategory="servergroups"
      pageName="settings"
      featureNav={<SettingsNav />}
    >
      <Panel
        title={serverId ? "Server Groups" : "All Server Groups"}
        secondaryTitle={<SearchInput />}
      >
        <PanelSection>Select a server group to view settings:</PanelSection>
        <Grid
          striped
          defaultSortBy="name"
          data={gridData}
          columns={[
            {
              field: "name",
              header: "Server Group",
              width: "minmax(30%,1fr)",
              renderer: ServerGroupNameCell,
            },
            {
              field: "type",
              header: "Type",
              width: "20%",
              className: "flex items-center",
              renderer: ServerGroupTypeCell,
            },
            {
              field: "clusterId",
              header: "Cluster ID",
              width: "minmax(20%,25%)",
              nullValue: "—",
            },
          ]}
        />
      </Panel>
    </PageContent>
  );
};

const ServerGroupNameCell = ({ rowData }: { rowData: RowDataType }) => {
  const { serverGroupEdit } = useRoutes();
  return (
    <span>
      <Link to={serverGroupEdit(rowData.id)}>{rowData.name}</Link>
    </span>
  );
};

const ServerGroupTypeCell = ({ rowData }: { rowData: RowDataType }) => {
  return rowData.type === "Cluster" ? (
    <Badge className="border font-medium !rounded-md !py-[2px] !px-[3px] !ml-0 !text-xs">
      Cluster
    </Badge>
  ) : (
    <Badge className="border font-medium !rounded-md !py-[2px] !px-[3px] !ml-0 !text-xs !bg-[#ede9fe] !text-[#6d28d9] !border-[#c4b5fd]">
      Server Group
    </Badge>
  );
};

export default ServerGroupSettingsList;
